import { Property } from '@model';
import { useProperties } from '@providers';
import { useMemo } from 'react';
import { HomeSectionHeading } from '../HomeSectionHeading.tsx';
import { AreaPropertiesTile } from './AreaPropertiesTile.tsx';
import './PropertiesByArea.scss';
import { useTranslation } from 'react-i18next';

export const PropertiesByArea = () => {
  const { t } = useTranslation();
  const propertiesModel = useProperties();

  const { areas, propertiesByArea } = useMemo(() => {
    if (!propertiesModel.properties?.length)
      return { areas: [], propertiesByArea: {} };

    const propertiesByArea = propertiesModel.properties.reduce(
      (acc, prop) => {
        const area = prop.area;
        if (!acc[area]) acc[area] = [];
        acc[area].push(prop);
        return acc;
      },
      {} as Record<string, Property[]>,
    );
    const areasByPropertyCount = Object.entries(propertiesByArea)
      .sort((a, b) => b[1].length - a[1].length)
      .map(([area]) => area);

    // Pick the top N areas with the most properties
    // Then randomly pick from the remaining areas to fill the other tiles
    // Probability of picking a random area is dependent on the number of properties in the area
    // so we pick a random property with uniform probability and choose the area
    const numTiles = 12;
    const numHighCountAreas = 6;
    let areas = areasByPropertyCount.slice(0, numHighCountAreas);
    for (let i = 0; i < numTiles - numHighCountAreas; i++) {
      const areasRandomPool = propertiesModel.properties.filter(
        (p) => !areas.includes(p.area),
      );
      const randomIndex = Math.floor(Math.random() * areasRandomPool.length);
      areas.push(areasRandomPool[randomIndex].area);
    }
    // Shuffle the areas
    areas = areas.sort(() => 0.5 - Math.random());
    return { areas, propertiesByArea };
  }, [propertiesModel.properties]);

  if (!propertiesModel.properties?.length) return <></>;

  return (
    <div
      className='section-bg-1 pt-115 pb-70'
      style={{
        backgroundImage: `url(https://rizosrealty.blob.core.windows.net/site-content/backgrounds/wide-l.jpg)`,
      }}
    >
      <div className='container use-gap'>
        <HomeSectionHeading
          title={t('propertiesByArea.title')}
          subtitle={t('propertiesByArea.subtitle')}
        />
        <div className='properties-by-area-grid row'>
          {areas.map((area) => (
            <AreaPropertiesTile
              key={area}
              properties={propertiesByArea[area]}
              area={area}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
